import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { Row, Col, Input, Select as AntSelect, Card as AntCard } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { CommissionTrackerService } from '../../services';

const { Option } = AntSelect;

const CommissionTracker = () => {
  // State management
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 25,
    totalRecords: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    isrName: '',
    month: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalRecords: 0,
    totalCommission: 0,
    totalRepCommission: 0,
    totalManagerCommission: 0,
    avgCommissionPerDeal: 0
  });

  // Dialog states
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({
    open: false,
    recordId: null,
    currentStatus: '',
    newStatus: '',
    notes: ''
  });

  // Load commission tracker data
  const loadCommissionData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryFilters = {
        ...filters,
        page: pagination.page + 1,
        limit: pagination.rowsPerPage
      };

      const response = await CommissionTrackerService.getCommissionTracker(queryFilters);
      
      setRecords(response.data.records);
      setPagination(prev => ({
        ...prev,
        totalRecords: response.data.pagination.totalRecords
      }));
      setSummaryStats(response.data.summary);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadCommissionData();
  }, [loadCommissionData]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Handle pagination changes
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({
      ...prev,
      rowsPerPage: event.target.value,
      page: 0
    }));
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    try {
      await CommissionTrackerService.updateCommissionStatus(
        statusUpdateDialog.recordId,
        statusUpdateDialog.newStatus,
        statusUpdateDialog.notes
      );
      
      setStatusUpdateDialog({
        open: false,
        recordId: null,
        currentStatus: '',
        newStatus: '',
        notes: ''
      });
      
      // Reload data
      loadCommissionData();
      
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle export to CSV
  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Get all records for export (remove pagination)
      const exportFilters = { ...filters, page: 1, limit: 10000 };
      const response = await CommissionTrackerService.getCommissionTracker(exportFilters);
      
      CommissionTrackerService.exportToCSV(response.data.records, 'commission-tracker');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    const statusColors = {
      Pending: 'warning',
      Approved: 'success',
      Paid: 'success',
      Disputed: 'error',
      Cancelled: 'default'
    };
    return statusColors[status] || 'default';
  };

  // Format currency
  const formatCurrency = (amount) => CommissionTrackerService.formatCurrency(amount);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCommissionData}
            disabled={loading}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem'
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={loading}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(24, 144, 255, 0.4)',
              }
            }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ color: 'primary.main', fontSize: '2.5rem', mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    variant="body2" 
                    sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                  >
                    Total Deals
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'text.primary',
                      fontSize: '2rem'
                    }}
                  >
                    {loading ? (
                      <Box 
                        sx={{ 
                          width: '60px', 
                          height: '32px', 
                          backgroundColor: 'grey.200',
                          borderRadius: 2,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                            '100%': { opacity: 1 }
                          }
                        }} 
                      />
                    ) : (
                      summaryStats.totalRecords || 0
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon sx={{ color: 'success.main', fontSize: '2.5rem', mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    variant="body2" 
                    sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                  >
                    Total Commission
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'success.dark',
                      fontSize: '2rem'
                    }}
                  >
                    {loading ? (
                      <Box 
                        sx={{ 
                          width: '80px', 
                          height: '32px', 
                          backgroundColor: 'grey.200',
                          borderRadius: 2,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                            '100%': { opacity: 1 }
                          }
                        }} 
                      />
                    ) : (
                      formatCurrency(summaryStats.totalCommission)
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={2.4}>
          <Card 
            sx={{ 
              height: '100%',
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.04)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ color: 'info.main', fontSize: '2.5rem', mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    variant="body2" 
                    sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}
                  >
                    Rep Commission
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'info.dark',
                      fontSize: '2rem'
                    }}
                  >
                    {loading ? (
                      <Box 
                        sx={{ 
                          width: '80px', 
                          height: '32px', 
                          backgroundColor: 'grey.200',
                          borderRadius: 2,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                            '100%': { opacity: 1 }
                          }
                        }} 
                      />
                    ) : (
                      formatCurrency(summaryStats.totalRepCommission)
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <AntCard 
        style={{ 
          marginBottom: 24,
          borderRadius: 16,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.04)'
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search records..."
                prefix={<SearchOutlined />}
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{ 
                  height: '48px',
                  borderRadius: 12,
                  fontSize: '14px'
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <AntSelect
                placeholder="Status"
                style={{ 
                  width: '100%', 
                  height: '48px'
                }}
                value={filters.status || undefined}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                {CommissionTrackerService.getCommissionStatuses().map(status => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </AntSelect>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <AntSelect
                placeholder="ISR"
                style={{ 
                  width: '100%', 
                  height: '48px'
                }}
                value={filters.isrName || undefined}
                onChange={(value) => handleFilterChange('isrName', value)}
                allowClear
              >
                {CommissionTrackerService.getISRList().map(isr => (
                  <Option key={isr.id} value={isr.name}>
                    {isr.name} {isr.isManager && '(Manager)'}
                  </Option>
                ))}
              </AntSelect>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <AntSelect
                placeholder="Month"
                style={{ 
                  width: '100%', 
                  height: '48px'
                }}
                value={filters.month || undefined}
                onChange={(value) => handleFilterChange('month', value)}
                allowClear
              >
                {CommissionTrackerService.getRecentMonths().map(month => (
                  <Option key={month} value={month}>
                    {month}
                  </Option>
                ))}
              </AntSelect>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Row gutter={[8, 0]}>
                <Col span={12}>
                  <Button 
                    variant="contained" 
                    startIcon={<RefreshIcon />}
                    onClick={loadCommissionData}
                    style={{ 
                      height: '48px', 
                      width: '100%',
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)'
                    }}
                  >
                    Refresh
                  </Button>
                </Col>
                <Col span={12}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setFilters({
                      status: '',
                      isrName: '',
                      month: '',
                      search: '',
                      startDate: '',
                      endDate: ''
                    })}
                    style={{ 
                      height: '48px', 
                      width: '100%',
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </AntCard>

      {/* Commission Records Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Deal ID</TableCell>
                <TableCell>Client Name</TableCell>
                <TableCell>ISR</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Rep Commission</TableCell>
                <TableCell>Manager Commission</TableCell>
                <TableCell>Total Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No commission records found
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record._id}>
                    <TableCell>
                      <Tooltip title={record.dealId}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                          {record.dealId}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={record.wellskyFullName}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {record.wellskyFullName}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{record.isrName}</TableCell>
                    <TableCell>{record.agencySubdomain}</TableCell>
                    <TableCell>
                      {record.startDate ? new Date(record.startDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{formatCurrency(record.repCommission)}</TableCell>
                    <TableCell>{formatCurrency(record.managerCommission)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(record.totalCommission)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.status}
                        color={getStatusColor(record.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{record.commissionMonth}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => setStatusUpdateDialog({
                          open: true,
                          recordId: record._id,
                          currentStatus: record.status,
                          newStatus: record.status,
                          notes: ''
                        })}
                      >
                        Update Status
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.totalRecords}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Status Update Dialog */}
      <Dialog
        open={statusUpdateDialog.open}
        onClose={() => setStatusUpdateDialog({ ...statusUpdateDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Commission Status</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={statusUpdateDialog.newStatus}
                label="New Status"
                onChange={(e) => setStatusUpdateDialog({
                  ...statusUpdateDialog,
                  newStatus: e.target.value
                })}
              >
                {CommissionTrackerService.getCommissionStatuses().map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Notes (optional)"
              multiline
              rows={3}
              value={statusUpdateDialog.notes}
              onChange={(e) => setStatusUpdateDialog({
                ...statusUpdateDialog,
                notes: e.target.value
              })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialog({ ...statusUpdateDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={!statusUpdateDialog.newStatus}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionTracker;
